import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { GuestClaim } from "../components/GuestClaim";

export default function CheckoutReturn() {
  const [sp] = useSearchParams();
  const sessionId = sp.get("session_id");
  const provider = sp.get("provider"); // 'paypal' or null (stripe default)
  const ourOrderId = sp.get("order_id");
  const paypalToken = sp.get("token"); // PayPal sends ?token=...&PayerID=...
  const isGuest = sp.get("guest") === "1";

  const [state, setState] = useState({ status: "checking", order_id: null });
  const attempts = useRef(0);
  const navigate = useNavigate();
  const captured = useRef(false);

  useEffect(() => {
    if (provider === "paypal" && paypalToken && ourOrderId) {
      if (captured.current) return;
      captured.current = true;
      (async () => {
        try {
          const { data } = await api.post("/checkout/paypal/capture", null, {
            params: { paypal_order_id: paypalToken, order_id: ourOrderId },
          });
          setState({ status: data.payment_status === "paid" ? "paid" : "error", order_id: data.order_id });
        } catch (e) {
          setState({ status: "error" });
        }
      })();
      return;
    }

    if (!sessionId) { navigate("/"); return; }
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(isGuest ? `/public/checkout/status/${sessionId}` : `/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState({ status: isGuest ? "guest_paid" : "paid", order_id: data.order_id, email: data.email, needs_password: data.needs_password });
          return;
        }
        if (data.status === "expired") { setState({ status: "expired" }); return; }
        attempts.current += 1;
        if (attempts.current >= 8) { setState({ status: "timeout" }); return; }
        timer = setTimeout(poll, 2000);
      } catch (e) {
        setState({ status: "error" });
      }
    };
    poll();
    return () => timer && clearTimeout(timer);
  }, [sessionId, provider, paypalToken, ourOrderId, navigate, isGuest]);

  return (
    <div className="container-page py-24 max-w-lg" data-testid="checkout-return">
      {state.status === "checking" && (
        <div className="text-center">
          <Loader2 className="h-10 w-10 mx-auto animate-spin text-muted-foreground" strokeWidth={1.5} />
          <h1 className="font-display text-3xl mt-6">Confirming your payment…</h1>
          <p className="text-sm text-muted-foreground mt-2 italic">Hold on while we provision your library.</p>
        </div>
      )}
      {state.status === "guest_paid" && (
        <GuestClaim sessionId={sessionId} email={state.email} needsPassword={state.needs_password} />
      )}
      {state.status === "paid" && (
        <div className="text-center">
          <CheckCircle2 className="h-12 w-12 mx-auto text-accent" strokeWidth={1.5} />
          <h1 className="font-display text-4xl mt-6">Order complete.</h1>
          <p className="text-sm text-muted-foreground mt-3 italic">Your titles are in your library and a receipt is on its way.</p>
          <div className="mt-8 flex gap-3 justify-center">
            <Link to="/library" className="bg-primary text-primary-foreground px-5 py-2.5 text-sm rounded-sm uppercase tracking-wide" data-testid="goto-library-cta">Go to My Library</Link>
            <Link to="/browse" className="border border-border px-5 py-2.5 text-sm rounded-sm uppercase tracking-wide">Keep browsing</Link>
          </div>
        </div>
      )}
      {(state.status === "expired" || state.status === "timeout" || state.status === "error") && (
        <div className="text-center">
          <XCircle className="h-12 w-12 mx-auto text-destructive" strokeWidth={1.5} />
          <h1 className="font-display text-4xl mt-6">Payment not completed</h1>
          <p className="text-sm text-muted-foreground mt-2 italic">Please try again from your cart.</p>
          <Link to="/cart" className="inline-block mt-6 border border-border px-5 py-2.5 text-sm rounded-sm uppercase tracking-wide">Back to cart</Link>
        </div>
      )}
    </div>
  );
}
